/**
 * Central keybinding definitions for the Consl application
 * 
 * This file contains all global keybindings using Vim-style conventions:
 * - g (go): Navigation between pages/views
 * - s (set): Setting/selecting UI elements (model, reasoning, attachments)
 * - t (toggle): Toggling UI state (sidebar, favorites)
 * - Single keys: Direct actions (new, input, search)
 * - Meta/Ctrl: App commands (command palette)
 */

import { createCrossPlatformBinding } from './utils.js';

/**
 * Helper function to check if keybindings should be allowed
 * Allows keybindings when not in an input, or when in command menu input
 */
const shouldAllowKeybinding = () => {
  const activeElement = document.activeElement;
  if (!activeElement) return true;
  
  const isInput = ['INPUT', 'TEXTAREA'].includes(activeElement.tagName);
  const isCommandMenuInput = activeElement.getAttribute('cmdk-input') !== null;
  
  return !isInput || isCommandMenuInput;
};

/**
 * Create keybinding definitions for the application
 * @param {Object} actions - Actions from AppContext
 * @param {Function} navigate - React Router navigate function
 * @param {Object} selectedConversationRef - Ref to current conversation
 * @param {Object} location - React Router location object
 * @returns {Object} - Keybinding definitions
 */
export const createAppKeybindings = (actions, navigate, selectedConversationRef, location) => {
  const {
    toggleSidebar,
    setSearchModal,
    toggleModelSelector,
    openFileInput,
    openThinkingModeSelector,
    createNewConversation,
    focusMainInput,
    favoriteConversation,
    deleteConversation,
    showConfirmation,
  } = actions;
  
  // Helper function to check if we're on a chat page
  const isChatPage = () => {
    return location?.pathname === '/chat' || location?.pathname.startsWith('/chat/');
  };

  // Get cross-platform primary modifier key (Cmd on Mac, Ctrl on Windows/Linux)
  const commandPaletteKey = createCrossPlatformBinding('k', { primary: true });

  return {
    // Navigation (g = "go to")
    'g c': {
      handler: () => {
        const selectedConversation = selectedConversationRef.current;
        if (selectedConversation?.conversationId) {
          navigate(`/chat/${selectedConversation.conversationId}`);
        } else {
          navigate('/chat');
        }
      },
      description: 'Go to chat (current conversation)',
      category: 'navigation'
    },
    
    'g s': {
      handler: () => navigate('/settings'),
      description: 'Go to settings page',
      category: 'navigation'
    },
    
    'g u': {
      handler: () => navigate('/usage'),
      description: 'Go to usage page',
      category: 'navigation'
    },
    
    // Command Menu (dynamically uses Cmd on Mac, Ctrl on Windows/Linux)
    [commandPaletteKey]: {
      handler: () => setSearchModal(),
      description: 'Open command menu',
      category: 'interface'
    },
    
    // Set actions (s = "set/select")
    's m': {
      handler: () => toggleModelSelector(),
      description: 'Set model (open model selector)',
      category: 'chat',
      when: () => shouldAllowKeybinding() && isChatPage(),
      disabledReason: 'Only available on chat pages'
    },
    
    's r': {
      handler: () => openThinkingModeSelector(),
      description: 'Set reasoning level (open thinking mode selector)',
      category: 'chat',
      when: () => shouldAllowKeybinding() && isChatPage(),
      disabledReason: 'Only available on chat pages'
    },
    
    's a': {
      handler: () => openFileInput(),
      description: 'Set attachment (open file picker)',
      category: 'chat',
      when: () => shouldAllowKeybinding() && isChatPage(),
      disabledReason: 'Only available on chat pages'
    },

    // Toggle actions (t = "toggle")
    't m': {
      handler: () => toggleSidebar(),
      description: 'Toggle menu/sidebar',
      category: 'interface'
    },
    
    't f': {
      handler: async () => {
        const selectedConversation = selectedConversationRef.current;
        if (selectedConversation?.conversationId) {
          await favoriteConversation(selectedConversation.conversationId);
        }
      },
      description: 'Toggle favorite for current conversation',
      disabledReason: 'No conversation selected',
      category: 'chat',
      when: () => {
        // Only when not typing in a regular input and we have a conversation
        const selectedConversation = selectedConversationRef.current;
        return shouldAllowKeybinding() && Boolean(selectedConversation?.conversationId);
      }
    },
    
    'Shift+D': {
      handler: async () => {
        const selectedConversation = selectedConversationRef.current;
        if (selectedConversation?.conversationId) {
          await showConfirmation({
            title: 'Delete Conversation',
            message: `Are you sure you want to delete "${selectedConversation.title || 'this conversation'}"? This action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            variant: 'destructive',
            onConfirm: async () => {
              await deleteConversation(selectedConversation.conversationId);
              // Navigate to chat home after deletion
              navigate('/chat');
            }
          });
        }
      },
      description: 'Delete current conversation',
      disabledReason: 'No conversation selected',
      category: 'chat',
      when: () => {
        // Only when not typing in a regular input and we have a conversation
        const selectedConversation = selectedConversationRef.current;
        return shouldAllowKeybinding() && Boolean(selectedConversation?.conversationId);
      }
    },
    
    // New Chat
    'n': {
      handler: () => createNewConversation(),
      description: 'Create new chat',
      category: 'chat',
      when: () => shouldAllowKeybinding()
    },
    
    
    // Input Focus
    'i': {
      handler: () => focusMainInput(),
      description: 'Focus chat input',
      category: 'chat',
      when: () => shouldAllowKeybinding() && isChatPage(),
      disabledReason: 'Only available on chat pages'
    },
    
    // Search/Command Palette (alternative)
    '/': {
      handler: () => setSearchModal(),
      description: 'Open search/command palette',
      category: 'interface',
      when: () => shouldAllowKeybinding()
    },
    
    // Note: Tab management (⌘T, ⌘W) is handled by Electron native menu via IPC
    // See electron/menu.js and AppKeybindings.jsx menu action handler
  };
};

/**
 * Keybinding categories for organization and help
 */
export const KEYBINDING_CATEGORIES = {
  navigation: {
    name: 'Navigation',
    description: 'Move between different pages and views',
    icon: '🧭'
  },
  interface: {
    name: 'Interface',
    description: 'Control UI elements and panels',
    icon: '🎛️'
  },
  chat: {
    name: 'Chat',
    description: 'Chat and conversation actions',
    icon: '💬'
  },
  tabs: {
    name: 'Tabs',
    description: 'Tab management and navigation',
    icon: '📑'
  },
};

/**
 * Get keybindings grouped by category
 * @param {Object} keybindings - All keybinding definitions
 * @returns {Object} - Keybindings grouped by category
 */
export const getKeybindingsByCategory = (keybindings) => {
  const grouped = {};
  
  Object.entries(keybindings).forEach(([sequence, binding]) => {
    const category = binding.category || 'other';
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push({
      sequence,
      ...binding
    });
  });
  
  return grouped;
};

/**
 * Default keybinding options
 */
export const DEFAULT_KEYBINDING_OPTIONS = {
  scope: 'global',
  preventDefault: true,
};